import { InvitationScreen, ResolveInviteResponseDto } from "@service/network/supplier/dto/resolve-invite-response.dto";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { RestError } from "../../../../../shared/lib/exceptions";
import { supplierQueryKeys } from "../../shared/query-keys";
import { resolveInvite } from "./supplier-registration.api";

export type ResolveInviteError = 404 | 503 | null;

// 404 → invalid or replaced invite (backend returns a uniform 404 for unknown id, wrong token and
// replaced token, by design — see supplier-registration.service.ts's loadVerifiedRow). An invite to
// an already-connected connection is not a 404: it resolves to the ALREADY_CONNECTED screen.
// Anything else → 503.
export const toResolveInviteError = (error: unknown): ResolveInviteError => {
  if (error instanceof RestError && error.statusCode === 404) {
    return 404;
  }
  return error ? 503 : null;
};

export const useResolveInvite = (
  connectionRequestId: string,
  token: string
): {
  data: ResolveInviteResponseDto | undefined;
  isLoading: boolean;
  error: ResolveInviteError;
} => {
  const shouldReturnMock = useIsBackendEmulationEnabled();

  const { data, isLoading, error } = useQuery({
    queryKey: supplierQueryKeys.resolveInvite(connectionRequestId, token),
    queryFn: () => resolveInvite(connectionRequestId, token),
    enabled: Boolean(connectionRequestId) && Boolean(token) && !shouldReturnMock,
    retry: false,
  });

  // TODO: AIM-2620: This is a temporary hack until the backend is ready. Remove after backend is there
  if (shouldReturnMock) {
    return {
      data: {
        screen: InvitationScreen.SELF_REGISTRATION,
        invitation: {
          buyerName: "Green Foods Inc.",
          supplierName: "Sunrise Organic Farms",
          respondBy: "2026-06-30",
        },
      },
      isLoading: false,
      error: null,
    };
  }

  return { data, isLoading, error: toResolveInviteError(error) };
};

const useIsBackendEmulationEnabled = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get("__emulate_registration_backend___") === "yes";
};
